import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import {
  getAdminUser,
  listAdminRole as listRoles,
  replaceRolesAdminUser as replaceRoles,
  updateStatusAdminUser as updateStatus,
} from '@api/generated/sdk.gen';
import { designTokens } from '@app/theme';
import { usePermissions } from '@refinedev/core';
import { Permissions } from '@shared/constants/permissions';
import { parseApiError } from '@shared/lib/api-error';
import { formatDateTime } from '@shared/lib/format-date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Result,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  notification,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { DevicesBlock } from './components/DevicesBlock';

const { Title, Text } = Typography;

/**
 * Карточка пользователя: метаданные, переключатель статуса (enabled/disabled),
 * редактор набора ролей, блок устройств с карантином.
 *
 * Гейтинг через `usePermissions`:
 * - просмотр — `users.read`
 * - смена статуса и подтверждение устройств — `users.manage`
 * - смена ролей — `users.roles.write`
 *
 * Использует SDK напрямую через `useQuery`/`useMutation` от tanstack-query —
 * Refine-data-provider не покрывает PATCH-status и PUT-roles без костылей.
 */
export function UserShowPage() {
  const { t } = useTranslation(['users', 'common', 'errors']);
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: perms } = usePermissions<string[]>({});
  const canManage = perms?.includes(Permissions.USERS_MANAGE) ?? false;
  const canEditRoles = perms?.includes(Permissions.USERS_ROLES_WRITE) ?? false;
  const canReadRoles = perms?.includes(Permissions.ROLES_READ) ?? false;

  const userQuery = useQuery({
    queryKey: ['admin-user', id],
    queryFn: async () => {
      const res = await getAdminUser({ path: { id } });
      if (!res.data) throw new Error('USER_NOT_FOUND');
      return res.data;
    },
    enabled: id !== '',
  });

  const rolesQuery = useQuery({
    queryKey: ['admin-roles'],
    queryFn: async () => {
      const res = await listRoles();
      return res.data ?? [];
    },
    enabled: canReadRoles && canEditRoles,
  });

  const currentRoleIds = useMemo(
    () => (userQuery.data?.roles ?? []).map((r) => r.id ?? '').filter(Boolean),
    [userQuery.data?.roles],
  );

  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  useEffect(() => {
    setSelectedRoleIds(currentRoleIds);
  }, [currentRoleIds]);

  const statusMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await updateStatus({ path: { id }, body: { enabled } });
      if (!res.data) throw new Error('INVALID_RESPONSE');
      return res.data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(['admin-user', id], updated);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      notification.success({
        message: t('users:card.statusUpdated'),
        placement: 'topRight',
      });
    },
    onError: (err: unknown) => {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      notification.error({
        message: t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
        placement: 'topRight',
      });
    },
  });

  const rolesMutation = useMutation({
    mutationFn: async (roleIds: string[]) => {
      const res = await replaceRoles({ path: { id }, body: { roleIds } });
      if (!res.data) throw new Error('INVALID_RESPONSE');
      return res.data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(['admin-user', id], updated);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      notification.success({
        message: t('users:card.rolesUpdated'),
        placement: 'topRight',
      });
    },
    onError: (err: unknown) => {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      notification.error({
        message: t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
        placement: 'topRight',
      });
    },
  });

  const rolesChanged = useMemo(() => {
    const current = currentRoleIds.slice().sort().join(',');
    const next = selectedRoleIds.slice().sort().join(',');
    return current !== next;
  }, [currentRoleIds, selectedRoleIds]);

  if (userQuery.isLoading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <Card style={{ borderColor: designTokens.slate200 }}>
        <Result
          status="404"
          title="USER_NOT_FOUND"
          subTitle={t('errors:USER_NOT_FOUND')}
          extra={<Button onClick={() => void navigate('/users')}>{t('users:card.back')}</Button>}
        />
      </Card>
    );
  }

  const user = userQuery.data;
  const roleOptions = (rolesQuery.data ?? []).map((r) => ({
    value: r.id ?? '',
    label: r.name ?? '',
    description: r.description,
  }));

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Space direction="vertical" size={4}>
        <Link to="/users" style={{ color: designTokens.slate500, fontSize: 13 }}>
          <ArrowLeftOutlined /> {t('users:card.back')}
        </Link>
        <Space size={12} align="baseline">
          <Title
            level={3}
            style={{
              margin: 0,
              fontWeight: 600,
              fontFamily: '"JetBrains Mono", monospace',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: -0.3,
            }}
          >
            {user.phone}
          </Title>
          <Tag
            color={user.enabled ? 'green' : 'red'}
            style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}
          >
            {user.enabled ? t('users:status.enabled') : t('users:status.disabled')}
          </Tag>
        </Space>
      </Space>

      <Card
        title={t('users:card.metadata')}
        style={{ borderColor: designTokens.slate200 }}
        styles={{ header: { borderBottomColor: designTokens.slate200, fontWeight: 600 } }}
      >
        <Descriptions
          column={{ xs: 1, sm: 1, md: 2 }}
          size="small"
          labelStyle={{
            color: designTokens.slate500,
            fontSize: 13,
            fontWeight: 500,
            width: 180,
          }}
          contentStyle={{ fontSize: 14 }}
        >
          <Descriptions.Item label={t('users:card.id')}>
            <Text
              code
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12,
                overflowWrap: 'anywhere',
              }}
            >
              {user.id}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('users:card.createdAt')}>
            <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatDateTime(user.createdAt)}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('users:card.lastLoginAt')}>
            <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
              {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : t('users:never')}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={t('users:card.status')}
        style={{ borderColor: designTokens.slate200 }}
        styles={{ header: { borderBottomColor: designTokens.slate200, fontWeight: 600 } }}
        extra={
          <Switch
            checked={user.enabled}
            loading={statusMutation.isPending}
            disabled={!canManage}
            onChange={(checked) => {
              statusMutation.mutate(checked);
            }}
            checkedChildren={t('users:status.enabled')}
            unCheckedChildren={t('users:status.disabled')}
          />
        }
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('users:card.statusDescription')}
        </Text>
        {!canManage && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={t('users:card.noPermissionEdit')}
          />
        )}
      </Card>

      <Card
        title={t('users:card.roles')}
        style={{ borderColor: designTokens.slate200 }}
        styles={{ header: { borderBottomColor: designTokens.slate200, fontWeight: 600 } }}
        extra={
          canEditRoles && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={rolesMutation.isPending}
              disabled={!rolesChanged || selectedRoleIds.length === 0}
              onClick={() => {
                rolesMutation.mutate(selectedRoleIds);
              }}
            >
              {t('users:card.rolesSave')}
            </Button>
          )
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('users:card.rolesDescription')}
          </Text>
          {canEditRoles ? (
            <Select
              mode="multiple"
              value={selectedRoleIds}
              onChange={setSelectedRoleIds}
              placeholder={t('users:card.roles')}
              loading={rolesQuery.isLoading}
              options={roleOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              tagRender={(props) => (
                <Tag
                  closable={props.closable}
                  onClose={props.onClose}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    marginInlineEnd: 4,
                  }}
                >
                  {props.label}
                </Tag>
              )}
            />
          ) : (
            <Space wrap size={4}>
              {(user.roles ?? []).map((r) => (
                <Tag
                  key={r.id}
                  style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, margin: 0 }}
                >
                  {r.name}
                </Tag>
              ))}
            </Space>
          )}
          {!canEditRoles && (
            <Alert type="info" showIcon message={t('users:card.noPermissionEdit')} />
          )}
        </Space>
      </Card>

      <DevicesBlock userId={id} canManage={canManage} />
    </Space>
  );
}
